import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SupportApi } from "@/api";
import { qk } from "./queryKeys";
import { toast } from "@/components/Toaster";
import type { SupportAttachmentRef, Ticket, TicketFilter, TicketPriority, TicketStatus } from "@/types";

export function useInboxes() {
  return useQuery({ queryKey: qk.supportInboxes(), queryFn: () => SupportApi.listInboxes() });
}
export function useAgents() {
  return useQuery({ queryKey: qk.supportAgents(), queryFn: () => SupportApi.listAgents() });
}

export function useTickets(filter: TicketFilter) {
  return useQuery({
    queryKey: qk.supportTickets(filter),
    queryFn: () => SupportApi.list(filter),
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: qk.supportTicket(id || ""),
    queryFn: () => SupportApi.get(id!),
    enabled: !!id,
  });
}

export function useTicketMessages(id: string | null) {
  return useQuery({
    queryKey: qk.supportMessages(id || ""),
    queryFn: () => SupportApi.messages(id!),
    enabled: !!id,
    refetchInterval: 15_000,           // pull new inbound messages quickly
  });
}

export function useReplyTicket(ticket_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      text: string;
      html?: string;
      attachment_ids?: string[];
      attachments?: SupportAttachmentRef[];
    }) => SupportApi.reply(ticket_id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.supportMessages(ticket_id) });
      qc.invalidateQueries({ queryKey: qk.supportTicket(ticket_id) });
      // Also invalidate the queue so the just-replied ticket bumps to the top
      // (updated_at changed on the server). Without this the queue keeps the
      // stale ordering until the agent navigates away and back.
      qc.invalidateQueries({ queryKey: qk.support });
    },
    // Surface failures — without this a failed reply silently loses the
    // agent's text + attachments and the button just re-enables, looking
    // like nothing happened. The Composer's onSuccess clears state, so the
    // caller must NOT clear on error; this toast + the absent onSuccess
    // keeps the draft intact for retry.
    onError: (e) => toast.error(`Reply failed: ${e instanceof Error ? e.message : String(e)}`),
  });
}

export function useInternalNote(ticket_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => SupportApi.addInternalNote(ticket_id, { text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.supportMessages(ticket_id) }),
  });
}

/** Hard-delete a ticket and all its dependent rows. Returns a mutation the
 *  ticket reader can call from a Trash button (after a confirm()). On success,
 *  invalidates the support cache so the deleted ticket vanishes from the list. */
export function useDeleteTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => SupportApi.delete(id),
    onSuccess: (_v, id) => {
      // Drop cached detail + messages + activity for this ticket id, then
      // refetch the lists so the deleted row stops showing.
      qc.removeQueries({ queryKey: qk.supportTicket(id), exact: false });
      qc.removeQueries({ queryKey: qk.supportMessages(id), exact: false });
      qc.invalidateQueries({ queryKey: qk.support });
      toast.success("Ticket deleted");
    },
    onError: (e) => toast.error(`Delete failed: ${(e as Error).message}`),
  });
}

export function useTicketMutation(id: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: qk.support });
  // Without onError handlers the UI would silently swallow PATCH failures —
  // a button click would appear to "do nothing", leaving the user thinking
  // the page is broken. Always surface failures as a toast.
  const onErr = (label: string) => (e: unknown) =>
    toast.error(`${label} failed: ${e instanceof Error ? e.message : String(e)}`);
  const ok = (label: string) => () => { refresh(); toast.success(label); };
  return {
    setStatus:   useMutation({ mutationFn: (s: TicketStatus) => SupportApi.setStatus(id, s),    onSuccess: ok("Status updated"),    onError: onErr("Status update") }),
    setPriority: useMutation({ mutationFn: (p: TicketPriority) => SupportApi.setPriority(id, p), onSuccess: ok("Priority updated"),  onError: onErr("Priority update") }),
    assign:      useMutation({ mutationFn: (uid: string | null) => SupportApi.assign(id, uid),   onSuccess: ok("Assignee updated"),  onError: onErr("Assign") }),
    setInbox:    useMutation({ mutationFn: (inboxId: string) => SupportApi.setInbox(id, inboxId), onSuccess: ok("Moved to inbox"),    onError: onErr("Move to inbox") }),
    addTag:      useMutation({ mutationFn: (tag: string) => SupportApi.addTag(id, tag),          onSuccess: ok("Tag added"),         onError: onErr("Tag add") }),
    removeTag:   useMutation({ mutationFn: (tag: string) => SupportApi.removeTag(id, tag),       onSuccess: ok("Tag removed"),       onError: onErr("Tag remove") }),
  };
}

// Optimistic example for setStatus (showcase pattern, not the full API)
export function useOptimisticSetStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: TicketStatus) => SupportApi.setStatus(id, status),
    onMutate: async (status) => {
      const key = qk.supportTicket(id);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Ticket>(key);
      qc.setQueryData<Ticket>(key, (t) => t ? { ...t, status } : t);
      return { prev };
    },
    onError: (_e, _s, ctx) => { if (ctx?.prev) qc.setQueryData(qk.supportTicket(id), ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.support }),
  });
}
